import React, { useEffect, useMemo, useState } from 'react';
import { ThumbsUp, Send, Pin } from 'lucide-react';
import { updatesService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1600&q=80';
const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

export const PostCard = ({
  title,
  content,
  imageUrl,
  authorName,
  authorMeta,
  timestamp,
  footer,
  fallbackToUnsplashImage = false,
  showActions = true,
  notificationId,
  initialLikes = 0,
  initialDislikes = 0,
  userReaction: initialUserReaction = null,
  managementActions = null,
}) => {
  const [likes, setLikes] = useState(initialLikes);
  const [dislikes, setDislikes] = useState(initialDislikes);
  const [userAction, setUserAction] = useState(initialUserReaction); // 'like', 'dislike', or null
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAuth();
  
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    if (!user?.id || !notificationId) return;

    const checkPinState = () => {
      const pinnedStr = localStorage.getItem(`pinnedNotice_${user.id}`);
      if (pinnedStr) {
        try {
          const pinnedData = JSON.parse(pinnedStr);
          setIsPinned(pinnedData.notificationId === notificationId);
        } catch (e) {
          console.error('Error parsing pinned notice', e);
          setIsPinned(false);
        }
      } else {
        setIsPinned(false);
      }
    };

    // Check immediately on mount
    checkPinState();

    // Listen to changes from other components (like the pinned bar)
    window.addEventListener('local-pin-change', checkPinState);

    return () => {
      window.removeEventListener('local-pin-change', checkPinState);
    };
  }, [user?.id, notificationId]);

  // Sync state when server-provided props change (e.g. after page refresh or pagination)
  useEffect(() => {
    setLikes(initialLikes);
    setDislikes(initialDislikes);
    setUserAction(initialUserReaction);
  }, [initialLikes, initialDislikes, initialUserReaction]);
  const avatarLetter = String(authorName || 'D').trim().charAt(0).toUpperCase();

  const handleLike = async () => {
    const previousLikes = likes;
    const previousDislikes = dislikes;
    const previousUserAction = userAction;

    // Optimistically update UI
    let newAction = userAction === 'like' ? null : 'like';
    setUserAction(newAction);

    if (newAction === 'like') {
      setLikes(prev => prev + 1);
      if (userAction === 'dislike') {
        setDislikes(prev => Math.max(0, prev - 1));
      }
    } else {
      setLikes(prev => Math.max(0, prev - 1));
    }

    if (notificationId) {
      try {
        const response = await updatesService.reactToUpdate(notificationId, {
          action: newAction,
        });

        if (response?.data) {
          setLikes(response.data.likes || 0);
          setDislikes(response.data.dislikes || 0);
          setUserAction(response.data.userReaction || null);
        }
      } catch (err) {
        console.error('Failed to update reaction', err);
        // Revert on error
        setLikes(previousLikes);
        setDislikes(previousDislikes);
        setUserAction(previousUserAction);
      }
    }
  };

  const handlePin = async () => {
    if (!user?.id) return;
    const pinKey = `pinnedNotice_${user.id}`;
    
    if (isPinned) {
      // Unpin
      localStorage.removeItem(pinKey);
      setIsPinned(false);
      window.dispatchEvent(new Event('local-pin-change'));
      return;
    }

    // Check if another notice is already pinned
    const existingPin = localStorage.getItem(pinKey);
    if (existingPin) {
      toast.warn('You can only pin one notice at a time. Please unpin the current one first.');
      return;
    }

    // Pin this notice
    let base64Image = null;
    if (displayImage) {
      try {
        const response = await fetch(displayImage);
        const blob = await response.blob();
        base64Image = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        console.error('Failed to convert image to base64 for local storage', e);
      }
    }

    const pinnedData = {
      notificationId,
      title,
      content,
      imageUrl: base64Image || displayImage,
      authorName,
      authorMeta,
      timestamp,
      likes,
      userReaction: userAction,
    };
    
    try {
      localStorage.setItem(pinKey, JSON.stringify(pinnedData));
      setIsPinned(true);
      window.dispatchEvent(new Event('local-pin-change'));
    } catch (e) {
      console.error('Failed to save to local storage', e);
      toast.error('Failed to pin notice. It might be too large.');
    }
  };


  const displayImage = useMemo(() => {
    if (imageUrl) return imageUrl;
    if (fallbackToUnsplashImage) return DEFAULT_IMAGE;
    return null;
  }, [imageUrl, fallbackToUnsplashImage]);

  const plainContentWithBreaks = useMemo(
    () =>
      String(content || '')
        .replace(/<br\s*\/?\s*>/gi, '\n')
        .replace(/<\/(p|div|li|h1|h2|h3|h4|h5|h6|blockquote)>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\n{2,}/g, '\n')
        .trim(),
    [content]
  );

  const shouldShowReadMore = useMemo(() => {
    const logicalLines = plainContentWithBreaks.split('\n').filter(Boolean).length;
    return logicalLines > 5 || plainContentWithBreaks.length > 320;
  }, [plainContentWithBreaks]);

  const handleShare = async () => {
    const shareText = `${title}\n\n${stripHtml(content)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: shareText });
        return;
      }
    } catch (_) {
      // fallback to clipboard
    }

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareText);
      } catch (_) {
        // ignore clipboard failure
      }
    }
  };

  const handleDownloadImage = async () => {
    if (!displayImage) return;

    const safeBaseName = String(title || 'notification-image')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'notification-image';

    try {
      const response = await fetch(displayImage);
      if (!response.ok) {
        throw new Error('Image fetch failed');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${safeBaseName}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (_) {
      const link = document.createElement('a');
      link.href = displayImage;
      link.download = `${safeBaseName}.jpg`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <article className="m-0 w-full rounded-3xl bg-surface-container-lowest border border-outline-variant/40 shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow">
      <header className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-primary-fixed flex items-center justify-center text-primary font-bold text-lg">
            {avatarLetter}
          </div>
          <h3 className="flex flex-col min-w-0 text-sm font-semibold text-primary">
            <span className="truncate">{authorName || 'CITNC Office'}</span>
            <span className="flex items-center gap-2 text-xs font-medium text-secondary truncate">
              <small className="truncate">{authorMeta || '@CITNC'}</small>
              {timestamp ? (
                <>
                  <span>·</span>
                  <small className="truncate">{timestamp}</small>
                </>
              ) : null}
            </span>
          </h3>
        </div>
      </header>

      <div className="mt-4 flex flex-col gap-4">
        {displayImage ? (
          <button type="button" className="w-full" onClick={handleDownloadImage}>
            <img
              src={displayImage}
              alt={title || 'Notification image'}
              className="w-full h-auto max-h-[420px] rounded-lg object-cover border border-slate-200 cursor-pointer"
            />
          </button>
        ) : null}
        {title ? <h4 className="text-lg font-bold text-primary">{title}</h4> : null}
        {content ? (
          <div
            className="text-base text-on-surface leading-relaxed prose prose-sm max-w-none"
            style={
              !isExpanded
                ? {
                    display: '-webkit-box',
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }
                : undefined
            }
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : null}
        {content && shouldShowReadMore ? (
          <button
            type="button"
            className="text-sm text-primary-container font-bold w-fit"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? 'Read less' : 'Read more...'}
          </button>
        ) : null}
      </div>

      {footer ? <div className="mt-4 rounded-2xl bg-surface-container-low p-4 text-xs text-on-surface-variant">{footer}</div> : null}
      {managementActions ? <div className="mt-4">{managementActions}</div> : null}

      {showActions ? (
      <div className="mt-6 flex justify-evenly gap-2 border-t border-outline-variant/50 pt-5">
        <button
          type="button"
          onClick={handleLike}
          className={`flex grow items-center justify-center gap-2 rounded-2xl px-4 py-2.5 transition ${
            userAction === 'like' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-surface-container text-primary hover:bg-secondary-container'
          }`}
        >
          <ThumbsUp size={16} className={userAction === 'like' ? 'fill-blue-500 text-blue-500' : ''} />
          <span className="inline font-bold text-[13px]">Like</span>
          {likes > 0 && (
            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold leading-none ${
              userAction === 'like'
                ? 'bg-blue-200 text-blue-800'
                : 'bg-slate-200 text-slate-600'
            }`}>
              {likes}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={handleShare}
          className="flex grow items-center justify-center gap-2 rounded-2xl px-4 py-2.5 transition bg-surface-container text-primary hover:bg-secondary-container"
        >
          <Send size={16} />
          <span className="inline font-bold text-[13px] max-sm:hidden">Share</span>
        </button>

        <button
          type="button"
          onClick={handlePin}
          className={`flex grow items-center justify-center gap-2 rounded-2xl px-4 py-2.5 transition ${
            isPinned 
              ? 'bg-amber-100 text-amber-700' 
              : 'bg-surface-container text-primary hover:bg-secondary-container'
          }`}
        >
          <Pin size={16} className={isPinned ? 'fill-amber-500 text-amber-500' : ''} />
          <span className="inline font-bold text-[13px]">Pin</span>
        </button>
      </div>
      ) : null}
    </article>
  );
};

export default PostCard;
